"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { X } from "lucide-react";

import { GiftIcon } from "@/components/icons/GiftIcon";
import { GridContainer } from "@/components/layout/GridContainer";
import { useMobileNavVisible } from "@/lib/hooks/useMobileNavVisible";

const HIDDEN_PATHS = ["/praxen"];

const STORAGE_KEY = "announcement_banner_dismissed";
const LOGO_BAR_HEIGHT = 44;

function BannerContent({ onDismiss }: { onDismiss: () => void }) {
  return (
    <GridContainer grid={false} className="py-2.5 flex items-center gap-3">
      <GiftIcon className="size-5 shrink-0 text-black" />
      <p className="text-copy-sm text-black flex-1">
        Du gehörst zu den ersten Dialetics Members. Sicher dir jetzt den{" "}
        <Link
          href="/membership"
          className="underline underline-offset-4 font-medium"
        >
          exklusiven Zugang →
        </Link>
      </p>
      <button
        type="button"
        onClick={onDismiss}
        className="shrink-0 text-black hover:text-black-80 transition-colors"
        aria-label="Banner schließen"
      >
        <X className="size-4" />
      </button>
    </GridContainer>
  );
}

export function AnnouncementBanner({ variant }: { variant?: "mobile" | "desktop" } = {}) {
  const [show, setShow] = useState(false);
  const [bannerHeight, setBannerHeight] = useState(0);
  const bannerRef = useRef<HTMLDivElement>(null);
  const navVisible = useMobileNavVisible();
  const pathname = usePathname();

  const hidden = HIDDEN_PATHS.some((p) => pathname === p || pathname.startsWith(p + "/"));

  useEffect(() => {
    if (hidden) return;
    const dismissed = sessionStorage.getItem(STORAGE_KEY);
    if (dismissed) return;
    fetch("/api/banner")
      .then((r) => r.json())
      .then((d) => {
        if (d.show) setShow(true);
      })
      .catch(() => {});
  }, [hidden]);

  const measure = useCallback(() => {
    if (bannerRef.current) {
      setBannerHeight(bannerRef.current.offsetHeight);
    }
  }, []);

  useEffect(() => {
    if (!show || variant === "desktop") return;
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [show, measure, variant]);

  useEffect(() => {
    if (variant !== "desktop") return;
    const val = show ? "40px" : "0px";
    document.documentElement.style.setProperty("--banner-height", val);
    return () => {
      document.documentElement.style.setProperty("--banner-height", "0px");
    };
  }, [show, variant]);

  if (!show || hidden) return null;

  const dismiss = () => {
    sessionStorage.setItem(STORAGE_KEY, "1");
    setShow(false);
  };

  if (variant === "desktop") {
    return (
      <div className="bg-sun-100 w-full">
        <BannerContent onDismiss={dismiss} />
      </div>
    );
  }

  return (
    <>
      {/* Mobile: fixed, follows MobileLogoBar */}
      <div
        ref={bannerRef}
        className="lg:hidden fixed left-0 right-0 z-40 bg-sun-100 transition-transform duration-300 ease-out"
        style={{
          top: 0,
          transform: navVisible
            ? `translateY(${LOGO_BAR_HEIGHT}px)`
            : "translateY(0)",
        }}
      >
        <BannerContent onDismiss={dismiss} />
      </div>
      {/* Spacer so content isn't hidden behind the fixed banner */}
      <div className="lg:hidden" style={{ height: bannerHeight }} />
    </>
  );
}
